import { SnackbarService } from './../shared/services/snackbar.service';
import { Injectable } from '@angular/core';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { CONSTANTS } from './constants';
import { FormControl } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class UtilService {
  minFileSizeBytes: Number = 1;
  maxFileSizeBytes: Number = 26214400; // 25Mb
  maxPackageSizeBytes: Number = 41943040; // 40Mb
  validExtensions: Array<string> = ['pdf', 'xls', 'xlsx', 'png', 'jpeg', 'jpg', 'doc', 'docx', 'txt', 'xml'];
  validMimeTypes: Array<string> = [
    'image/png',
    'image/jpeg',
    'image/pjpeg',
    'application/pdf',
    'application/msword',
    'application/excel',
    'application/x-excel',
    'application/x-msexcel',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/xml',
    'text/xml',
    'text/plain'];

  constructor(private snackbarService: SnackbarService) { }

  // Function to show snack bar
  public showSnackbar(message: string, action?: string): void {
    this.snackbarService.showSnackbar(message, action ? action : CONSTANTS.GLOBAL.OK.toUpperCase());
  }

  private b64ToUint6 = (nChr) => {
    return nChr > 64 && nChr < 91 ?
      nChr - 65
      : nChr > 96 && nChr < 123 ?
        nChr - 71
        : nChr > 47 && nChr < 58 ?
          nChr + 4
          : nChr === 43 ?
            62
            : nChr === 47 ?
              63
              :
              0;
  }

  private base64DecToArr = (sBase64: string, nBlocksSize?) => {
    const sB64Enc = sBase64.replace(/[^A-Za-z0-9\+\/]/g, '');
    const nInLen = sB64Enc.length;
    // tslint:disable-next-line: no-bitwise
    const nOutLen = nBlocksSize ? Math.ceil((nInLen * 3 + 1 >> 2) / nBlocksSize) * nBlocksSize : nInLen * 3 + 1 >> 2;
    const taBytes = new Uint8Array(nOutLen);

    for (let nMod3, nMod4, nUint24 = 0, nOutIdx = 0, nInIdx = 0; nInIdx < nInLen; nInIdx++) {
      nMod4 = nInIdx & 3;
      // tslint:disable-next-line: no-bitwise
      nUint24 |= this.b64ToUint6(sB64Enc.charCodeAt(nInIdx)) << 18 - 6 * nMod4;
      if (nMod4 === 3 || nInLen - nInIdx === 1) {
        for (nMod3 = 0; nMod3 < 3 && nOutIdx < nOutLen; nMod3++, nOutIdx++) {
          // tslint:disable-next-line: no-bitwise
          taBytes[nOutIdx] = nUint24 >>> (16 >>> nMod3 & 24) & 255;
        }
        nUint24 = 0;
      }
    }
    return taBytes;
  }

  private getTotalSize = (filesToUpload: Array<File>): number => {
    let sum = 0;
    filesToUpload.forEach(f => sum = sum + f.size);
    return sum;
  }

  public validateSize = (file: File, filePackage: Array<File>): boolean => {
    let msg = '';
    if (file.size < this.minFileSizeBytes) {
      msg = 'O arquivo não pode ser vazio.';
      this.showSnackbar(msg);
      return false;
    }

    if (file.size > this.maxFileSizeBytes) {
      msg = 'O tamanho do arquivo ultrapassa o limite de 25mb.';
      this.showSnackbar(msg);
      return false;
    }

    if (!this.checkExistenceByName(file.name, filePackage)) {
      if ((this.getTotalSize(filePackage) + file.size) > this.maxPackageSizeBytes) {
        msg = 'O tamanho total ultrapassa o limite de 40mb.';
        this.showSnackbar(msg);
        return false;
      }
    }

    return true;
  }

  public checkExtension = (file: File): boolean => {
    const fileExtension: string = file.name.split('.').pop();
    const mimeType = file.type;
    let msg = '';

    if (!this.validExtensions.includes(fileExtension.toLowerCase())) {
      msg = 'O tipo do arquivo não é permitido: ' + fileExtension.toLowerCase();
      this.showSnackbar(msg);
      return false;
    }

    if (!this.validMimeTypes.includes(mimeType.toLowerCase())) {
      msg = 'O tipo do arquivo não é permitido: ' + mimeType.toLowerCase();
      this.showSnackbar(msg);
      return false;
    }
    return true;
  }

  public checkSizeNameAttachments = (file: File): boolean => {
    if (file.name.length > 200) {
      const msg = 'O nome do arquivo deve conter até 200 caracteres.';
      this.showSnackbar(msg);
      return false;
    }

    return true;
  }

  private checkExistenceByName = (name: string, files: Array<File>): boolean => {
    return (files.findIndex(upload => upload.name === name) > -1);
  }

  public downloadFileBlob(blob: Blob, fileName: string) {
    try {
      if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveBlob(blob, fileName);
      } else {
        const elem = window.document.createElement('a');
        elem.href = window.URL.createObjectURL(blob);
        elem.download = fileName;
        document.body.appendChild(elem);
        elem.click();
        document.body.removeChild(elem);
      }
    } catch (e) {
      throw Error(e);
    }
  }

  public getSizeFileText(data: any): string {
    if (typeof data !== 'string') {
      data = data.size;
    }
    return (data / Math.pow(1024, 2)).toFixed(2).replace(/[.,]00$/, '') + ' ' + 'MB';
  }

  public convertByteJsonToBlob(contentFile: string, contentType?: string): Blob {
    const uriEncodedString = decodeURIComponent(contentFile);
    const typeFile = contentType ? contentType : 'application/octet-stream';

    return new Blob([this.base64DecToArr(uriEncodedString)], { type: typeFile });
  }

  public validateFileToUpload(event, filesToUpload, eventFilesUpload?) {
    const maxLenghtNameDocument = 40;
    if (event.target.files[0]) {
      const file: any = event.target.files[0];
      if (
        file.name && !this.checkExistenceByName(file.name, filesToUpload)
        && this.validateSize(file, filesToUpload) && this.checkExtension(file)
        && this.checkSizeNameAttachments(file)
      ) {
        const infoNameDocument = file.name.split('.');

        if (infoNameDocument[0].length > maxLenghtNameDocument) {
          file.nameSimplyfied = `${infoNameDocument[0].substring(0, maxLenghtNameDocument)}...${infoNameDocument[1]}`;
        }
        filesToUpload.push(file);

        if (eventFilesUpload) {
          eventFilesUpload.emit(filesToUpload);
        }
      }
    }
  }

  public convertPayloadFileToFormData(payload: object, namePayload: string, files: Array<File>): FormData {
    const formData = new FormData;
    formData.append(namePayload, new Blob([JSON.stringify(payload)],
      {
        type: 'application/json'
      }));
    Array.from(files).forEach(file => formData.append('files', file, file.name));
    return formData;
  }

  public handleStatusProgressLoading(event, value: Number, message: string): boolean {
    let isCompleted = false;
    if (event.type === HttpEventType.UploadProgress) {
      value = Math.round(100 * event.loaded / event.total);

    } else if (event instanceof HttpResponse) {
      this.showSnackbar(message);
      isCompleted = true;
    }
    return isCompleted;
  }

  public containsTypeOpenImpl(name: string): Boolean {
    const nameNormalize = this.accentsTidy(name);
    const containsOpenType = nameNormalize.toLowerCase() === 'abertura';
    const containsOpenTypeImp = nameNormalize.toLowerCase().includes('implantacao');
    return containsOpenType || containsOpenTypeImp;
  }

  accentsTidy(s: string) {
    let r = s.toLowerCase();
    let non_asciis = { 'a': '[àáâãäå]', 'ae': 'æ', 'c': 'ç', 'e': '[èéêë]', 'i': '[ìíîï]', 'n': 'ñ', 'o': '[òóôõö]', 'oe': 'œ', 'u': '[ùúûűü]', 'y': '[ýÿ]' };
    for (let i in non_asciis) { r = r.replace(new RegExp(non_asciis[i], 'g'), i); }
    return r;
  }

  public setValuePropertyHTML(id: string, value: any) {
    (<HTMLInputElement>document.getElementById(id)).value = value;
  }

  public requestFocus(id: string) {
    (<HTMLInputElement>document.getElementById(id)).focus();
  }

  public getFirstElememtByClassName(className: string): HTMLInputElement {
    let item = <HTMLInputElement>document.getElementsByClassName(className)[0];
    return item;
  }

  public requestFocusOnFirstOcurrence(className: string) {
    let item = <HTMLInputElement>document.getElementsByClassName(className)[0];
    if (item != undefined) {
      item.focus();
    }
  }

  public isHomePageOrBrowserWarning(): boolean {
    return window.location.hash === '#/' || window.location.hash === '#/browser-warning';
  }

  public handleNullOrUndefined(element: any): boolean {
    return element !== null && element !== undefined ? element : '';
  }
}

export const isMobile = window.innerWidth <= 760;
export const datePad = (date: number): string => {
  if (!String.prototype.padStart) {
    String.prototype.padStart = function padStart(targetLength, padString) {
      targetLength = targetLength >> 0;
      padString = String((typeof padString !== 'undefined' ? padString : ' '));
      if (this.length > targetLength) {
        return String(this);
      }
      else {
        targetLength = targetLength - this.length;
        if (targetLength > padString.length) {
          padString += padString.repeat(targetLength / padString.length);
        }
        return padString.slice(0, targetLength) + String(this);
      }
    };
  }
  return `${date}`.padStart(2, '0');
}

export function formatCurrency(number: number): string {
  const value = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  return value.format(number);
}

export function formatZeroPad(num: number): string {
  return String("00000000" + num).slice(-8);
}

export function preventWeekendFilter(d: Date | null): boolean {
  const day = (d || new Date()).getDay();
  // Prevent Saturday and Sunday from being selected.
  return day !== 0 && day !== 6;
}

export function addDateMask(evento): any {
  var v = evento.target.value;
  if (evento.key != 'Backspace') {
    if (v.match(/^\d{2}$/) !== null) {
      evento.target.value = v + '/';
    } else if (v.match(/^\d{2}\/\d{2}$/) !== null) {
      evento.target.value = v + '/';
    }
  }
  return evento.target.value
}

export function ptDateMask(control: FormControl): { [key: string]: any } {
  let ptDatePattern = /^(((0[1-9]|[12]\d|3[01])\/(0[13578]|1[02])\/((19|[2-9]\d)\d{2}))|((0[1-9]|[12]\d|30)\/(0[13456789]|1[012])\/((19|[2-9]\d)\d{2}))|((0[1-9]|1\d|2[0-8])\/02\/((19|[2-9]\d)\d{2}))|(29\/02\/((1[6-9]|[2-9]\d)(0[48]|[2468][048]|[13579][26])|((16|[2468][048]|[3579][26])00))))$/g;

  const val = control.value;
  if (val != null && val != '' && val instanceof String) {
    if (!val.match(ptDatePattern))
      return { "ptDate": true };
  }
  return null;
}

export function padEndSuport() {
  if (!String.prototype.padEnd) {
    String.prototype.padEnd = function padEnd(targetLength, padString) {
      targetLength = targetLength >> 0;
      padString = String((typeof padString !== 'undefined' ? padString : ' '));

      if (this.length > targetLength) {
        return String(this);
      } else {
        targetLength = targetLength - this.length;

        if (targetLength > padString.length) {
          padString += padString.repeat(targetLength / padString.length);
        }

        return String(this) + padString.slice(0, targetLength);
      }
    };
  }
}

//normalização para analytics
export interface SanitizeOptions {
  allowedSeparators?: string; // ex.: ':-'
  replacement?: string; // ex.: '-'
  trimEdges?: boolean; // remover separadores no início/fim
}

// Fallback simples para PT-BR caso normalize não esteja disponível (IE11).
const DIACRITICS_MAP: Record<string, string> = {
  'Á': 'A', 'À': 'A', 'Â': 'A', 'Ã': 'A', 'Ä': 'A', 'á': 'a', 'à': 'a', 'â': 'a', 'ã': 'a', 'ä': 'a',
  'É': 'E', 'È': 'E', 'Ê': 'E', 'Ë': 'E', 'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
  'Í': 'I', 'Ì': 'I', 'Î': 'I', 'Ï': 'I', 'í': 'i', 'ì': 'i', 'î': 'i', 'ï': 'i',
  'Ó': 'O', 'Ò': 'O', 'Ô': 'O', 'Õ': 'O', 'Ö': 'O', 'ó': 'o', 'ò': 'o', 'ô': 'o', 'õ': 'o', 'ö': 'o',
  'Ú': 'U', 'Ù': 'U', 'Û': 'U', 'Ü': 'U', 'ú': 'u', 'ù': 'u', 'û': 'u', 'ü': 'u',
  'Ç': 'C', 'ç': 'c', 'Ñ': 'N', 'ñ': 'n'
};

function removeDiacritics(input: string): string {
  if (typeof String.prototype.normalize === 'function') {
    return input.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  return input.replace(/[^\x20-\x7E]/g, (ch) => (DIACRITICS_MAP[ch] !== undefined ? DIACRITICS_MAP[ch] : ch));
}
//
// Escapa caracteres especiais em classes de caracteres de RegExp ([], ex.: '-', '' e '^')
function escapeForCharClass(s: string): string {
  return s.replace(/[-\^]/g, '\$&');
}

// remove hífens no início e fim sem regex dinâmica
function trimHyphens(input: string): string {
  if (!input) return '';
  const HYPHEN = 45; // '-'
  let start = 0;
  let end = input.length;

  while (start < end && input.charCodeAt(start) === HYPHEN) start++;
  while (end > start && input.charCodeAt(end - 1) === HYPHEN) end--;

  return input.slice(start, end);
}

// Colapsa todos os ":" consecutivos em um único ":" e remove ":" nas bordas
function collapseAndTrimColons(value: string): string {
  if (!value) return '';
  const COLON = 58; // ':'

  // trim ":" nas bordas
  let start = 0;
  let end = value.length;
  while (start < end && value.charCodeAt(start) === COLON) start++;
  while (end > start && value.charCodeAt(end - 1) === COLON) end--;
  if (start === end) return '';

  // colapsa ":" no miolo
  let out = '';
  let prevWasColon = false;
  for (let i = start; i < end; i++) {
    const code = value.charCodeAt(i);
    if (code === COLON) {
      if (!prevWasColon) {
        out += ':';
        prevWasColon = true;
      }
    } else {
      out += value.charAt(i);
      prevWasColon = false;
    }
  }
  return out;
}

// Remove o replacement (ex.: '-') imediatamente antes/depois de ":" (ex.: "-:" -> ":", ":-" -> ":")
// Sem regex, compatível com TS 3.2
function normalizeReplacementAroundColon(value: string, replacement: string): string {
  if (!value) return '';
  if (!replacement || replacement.length !== 1) return value;
  const REP = replacement.charCodeAt(0);
  const COLON = 58;

  let out = '';
  for (let i = 0; i < value.length; i++) {
    const curr = value.charCodeAt(i);
    const next = i + 1 < value.length ? value.charCodeAt(i + 1) : -1;

    // Caso "-:" (ou REP+":"), pulamos o replacement; o ":" será processado na próxima iteração
    if (curr === REP && next === COLON) {
      continue;
    }

    if (curr === COLON) {
      // Adiciona ":" e pula todos os replacements seguintes imediatos, cobrindo ":-", ":- -", etc.
      out += ':';
      while (i + 1 < value.length && value.charCodeAt(i + 1) === REP) {
        i++;
      }
      continue;
    }

    out += value.charAt(i);
  }
  return out;
}



function isWhitespaceCode(c: number): boolean {
  // espaço, tab, LF, VT, FF, CR
  return c === 32 || c === 9 || c === 10 || c === 11 || c === 12 || c === 13;
}

// Split por '>', ':', '/', '|' com espaços opcionais
export function splitBreadcrumbPartsNoRegex(input: string): string[] {
  let s = input != null ? String(input) : '';
  if (!s) return [];

  let parts: string[] = [];
  let buf = '';
  let len = s.length;

  // códigos ASCII dos separadores
  const GT = 62;    // '>'
  const SL = 47;    // '/'
  const PIPE = 124; // '|'
  const COLON = 58; // ':'

  for (let i = 0; i < len; i++) {
    let code = s.charCodeAt(i);

    if (code === GT || code === SL || code === PIPE || code === COLON) {
      // fecha token atual
      let token = buf.trim();
      if (token) parts.push(token);
      buf = '';

      // pula espaços e separadores consecutivos
      while (i + 1 < len) {
        let nextCode = s.charCodeAt(i + 1);
        if (nextCode === GT || nextCode === SL || nextCode === PIPE || nextCode === COLON || isWhitespaceCode(nextCode)) {
          i++;
        } else {
          break;
        }
      }
    } else {
      buf += s.charAt(i);
    }
  }

  // último token
  let last = buf.trim();
  if (last) parts.push(last);

  return parts;
}

function trimEdgeSlashes(s: string): string {
  if (!s) return '';
  const SLASH = 47;
  let start = 0;
  let end = s.length;
  while (start < end && s.charCodeAt(start) === SLASH) start++;
  while (end > start && s.charCodeAt(end - 1) === SLASH) end--;
  return s.slice(start, end);
}

function collapseSlashes(s: string): string {
  if (!s) return '';
  const SLASH = 47;
  let out = '';
  let prevSlash = false;
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    if (code === SLASH) {
      if (!prevSlash) {
        out += '/';
        prevSlash = true;
      }
    } else {
      out += s.charAt(i);
      prevSlash = false;
    }
  }
  return out;
}

function trimTrailingSlashes(s: string): string {
  if (!s) return '';
  const SLASH = 47;
  let end = s.length;
  while (end > 0 && s.charCodeAt(end - 1) === SLASH) end--;
  return s.slice(0, end);
}

// Colapsa múltiplos slashes e remove slashes finais (sem remover o inicial)
export function normalizeSlashesNoRegex(s: string): string {
  return trimTrailingSlashes(collapseSlashes(s));
}

export function sanitizeLabel(
  input: string,
  opts: SanitizeOptions = { allowedSeparators: ':-', replacement: '-', trimEdges: true }
): string {
  const allowedSeparatorsRaw = (opts && opts.allowedSeparators != null) ? opts.allowedSeparators : ':-';
  const replacement = (opts && opts.replacement != null) ? opts.replacement : '-';
  const trimEdges = (opts && typeof opts.trimEdges === 'boolean') ? opts.trimEdges : true;
  const allowedSeparators = escapeForCharClass(allowedSeparatorsRaw);

  let s = (input != null ? input : '').toString();
  if (!s) return '';

  s = removeDiacritics(s).toLowerCase();

  // Converte qualquer sequência de caracteres inválidos para o separador escolhido
  s = s.replace(new RegExp(`[^a-z0-9${allowedSeparators}]+`, 'g'), replacement);

  // Colapsa separadores repetidos
  const repEsc = escapeForCharClass(replacement);
  s = s.replace(new RegExp(`${repEsc}{2,}`, 'g'), replacement);

  // Normaliza hifens próximos de ":" (evita "-:" e ":-")
  s = s.replace(new RegExp(`${repEsc}+:`, 'g'), ':');
  s = s.replace(new RegExp(`:${repEsc}+`, 'g'), ':');

  // Colapsa múltiplos ":" e remove ":" nas bordas
  s = normalizeReplacementAroundColon(s, replacement);
  s = collapseAndTrimColons(s);

  // Remove separadores nas bordas, se configurado
  if (trimEdges) {
    const repEscPattern = new RegExp(`^${repEsc}+|${repEsc}+$`);
    s = s.replace(repEscPattern, '');
  }

  // Remove traço final, se existir
  if (s.endsWith('-')) {
    s = s.slice(0, -1);
  }

  return s;
}

// Caso receba uma string inteira já com separadores, pode sanitizar direto
export function sanitizeDetail(detail: string): string {
  return sanitizeLabel(detail, { allowedSeparators: ':-', replacement: '-', trimEdges: true });
}

// Remove acentos (sem depender de normalize) e normaliza para slug simples
export function removeAccentsPtBR(value: string): string {
  if (!value) return '';
  return value
    .replace(/[ÁÀÂÃÄ]/g, 'A').replace(/[áàâãä]/g, 'a')
    .replace(/[ÉÈÊË]/g, 'E').replace(/[éèêë]/g, 'e')
    .replace(/[ÍÌÎÏ]/g, 'I').replace(/[íìîï]/g, 'i')
    .replace(/[ÓÒÔÕÖ]/g, 'O').replace(/[óòôõö]/g, 'o')
    .replace(/[ÚÙÛÜ]/g, 'U').replace(/[úùûü]/g, 'u')
    .replace(/[Ç]/g, 'C').replace(/[ç]/g, 'c');
}
export function tryDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
export function slugify(value: string): string {
  if (!value) return '';
  const decoded = tryDecode(value);
  const noAccents = removeAccentsPtBR(decoded);
  const normalized = noAccents.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return trimHyphens(normalized);
}

export function splitBreadcrumbByChevronOnly(input: string): string[] {
  const s = input != null ? String(input) : '';
  if (!s) return [];
  const GT = 62; // '>'
  const parts: string[] = [];
  let buf = '';

  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    if (code === GT) {
      const token = buf.trim();
      if (token) parts.push(token);
      buf = '';
      // pular '>' e espaços consecutivos
      while (i + 1 < s.length) {
        const next = s.charCodeAt(i + 1);
        if (next === GT || isWhitespaceCode(next)) {
          i++;
        } else {
          break;
        }
      }
    } else {
      buf += s.charAt(i);
    }
  }

  const last = buf.trim();
  if (last) parts.push(last);

  return parts;
}

export function normalizePath(
  url: string,
  options?: { prefix?: string; fallback?: string }
): string {
  const opts = options || {};
  const prefix = trimEdgeSlashes((opts.prefix || '/iss').toLowerCase());
  const fallback = opts.fallback || '/iss/home';

  // Remove query/hash e matrix params
  const raw = (url || '');
  const noQuery = raw.split('?')[0];
  const noHash = noQuery.split('#')[0];
  const noMatrix = noHash.replace(/;[^/]+/g, '');

  // Sanitiza cada segmento do path
  const segs = noMatrix
    .split('/')
    .map(function (s) { return slugify(s); })
    .filter(function (s) { return !!s; });

  // Helper para sanitizar e retornar fallback
  function sanitizedFallback(): string {
    const fbSegs = (fallback || '/iss/home')
      .split('/')
      .map(function (s) { return slugify(s); })
      .filter(function (s) { return !!s; });
    let fbPath = '/' + fbSegs.join('/');
    fbPath = normalizeSlashesNoRegex(fbPath);

    return fbPath;
  }

  // Prefixo em segmentos (para evitar duplicação e tratar "apenas prefixo")
  const baseSegs = prefix
    ? prefix.split('/').map(function (s) { return slugify(s); }).filter(function (s) { return !!s; })
    : [];

  // Se não houver path, usa fallback
  if (!segs.length) {
    return sanitizedFallback();
  }

  // Evita duplicação do prefixo e trata caso "apenas prefixo"
  let resultSegs: string[];
  let startsWithBase = baseSegs.length > 0 && segs.slice(0, baseSegs.length).join('/') === baseSegs.join('/');

  if (startsWithBase) {
    if (segs.length === baseSegs.length) {
      // path é exatamente "/iss" ou "/iss/"
      return sanitizedFallback();
    }
    // Já tem o prefixo corretamente uma única vez
    resultSegs = segs;
  } else {
    resultSegs = baseSegs.concat(segs);
  }

  let result = '/' + resultSegs.join('/');
  result = normalizeSlashesNoRegex(result);

  // Safety extra: se por algum motivo ficou apenas o prefixo, usa fallback
  if (baseSegs.length && result === '/' + baseSegs.join('/')) {
    return sanitizedFallback();
  }

  return result;
}

export function detailFromBreadcrumb(input: string, contexto?: string): string {
  const parts = splitBreadcrumbByChevronOnly(input);
  const slugs = parts.map(slugify).filter(Boolean);
  return contexto ? contexto + slugs.join(':') : 'menu-' + slugs.join(':');
}

export function normalizePathFromBreadcrumb(
  input: string,
  options?: { prefix?: string; fallback?: string }
): string {
  const opts = options || {};
  const prefix = trimEdgeSlashes((opts.prefix || '/iss').toLowerCase());
  const fallback = opts.fallback || '/iss/home';

  // Separar exclusivamente por '>'
  const parts = splitBreadcrumbByChevronOnly(input);

  // Slugify cada parte
  const slugs = parts
    .map((p) => slugify(p))
    .filter((p) => !!p);

  function sanitizedFallback(): string {
    const fbSegs = (fallback || '/iss/home')
      .split('/')
      .map((s) => slugify(s))
      .filter((s) => !!s);
    let fbPath = '/' + fbSegs.join('/');
    fbPath = normalizeSlashesNoRegex(fbPath);
    return fbPath;
  }

  if (!slugs.length) {
    return sanitizedFallback();
  }

  const baseSegs = prefix
    ? prefix.split('/').map((s) => slugify(s)).filter((s) => !!s)
    : [];

  const startsWithBase =
    baseSegs.length > 0 &&
    slugs.slice(0, baseSegs.length).join('/') === baseSegs.join('/');

  let resultSegs: string[];
  if (startsWithBase) {
    if (slugs.length === baseSegs.length) {
      return sanitizedFallback();
    }
    resultSegs = slugs;
  } else {
    resultSegs = baseSegs.concat(slugs);
  }

  let result = '/' + resultSegs.join('/');
  result = normalizeSlashesNoRegex(result);

  if (baseSegs.length && result === '/' + baseSegs.join('/')) {
    return sanitizedFallback();
  }

  return result;
}

export function sanitizeOperatorId(input: any): string | null {
  const OPERATOR_ID_REGEX_STRICT = new RegExp('^[a-z0-9|:]+(-[a-z0-9|:]+)*(\\-[a-z0-9|:]+(-[a-z0-9|:]+)*)*$');

  if (input === null || input === undefined) return null;

  // 1) Stringify + trim + decode (caso venha percent-encoded) + remove acentos + lowercase
  let s = String(input).trim();
  if (!s) return null;

  // Reuso de utilitários já presentes no arquivo
  s = tryDecode(s);
  s = removeAccentsPtBR(s).toLowerCase();

  // 2) Substitui qualquer caractere NÃO permitido por hífen (mantém a-z, 0-9, |, :, e "-")
  s = s.replace(/[^a-z0-9|:\-]+/g, '-');

  // 3) Colapsa múltiplos hífens
  s = s.replace(/-+/g, '-');

  // 4) Remove hífens de início/fim
  s = trimHyphens(s);

  // 5) Se vazio após limpeza, invalida
  if (!s) return null;

  // 6) Validação final rigorosa com a regex informada
  if (!OPERATOR_ID_REGEX_STRICT.test(s)) {
    return null;
  }

  return s;
}

export function sanitizeSearchTerm(input: any, maxLen: number = 64): string {
  let s = (input != null ? String(input) : '').trim();
  if (!s) return '';

  // Caso venha percent-encoded (ex.: "%C3%81"), decodifica com fallback
  s = tryDecode(s);

  // Normaliza para slug controlado usando separadores permitidos ':-'
  // sanitizeLabel já remove diacríticos, põe em minúsculas,
  // converte sequências inválidas para '-', colapsa e remove nas bordas.
  s = sanitizeLabel(s, { allowedSeparators: ':-', replacement: '-', trimEdges: true });
  if (!s) return '';

  // Limite de tamanho por segurança
  if (s.length > maxLen) s = s.slice(0, maxLen);

  // Validação estrita: segmentos alfanuméricos separados por '-' e/ou ':'
  const STRICT = /^[a-z0-9]+(?:[:\-][a-z0-9]+)*$/;
  return STRICT.test(s) ? s : '';
}
